import { BundleId } from "@mecontrol/common";
import { ME, perfNow, MeControlError, createError, w, getRootDomainUrl, isValidURL } from "../utilities";
import { logTelemetryEvent } from "../logger";
import { Promise, timeout } from "../promise";
import { queueLength, popBundle } from './queue';
import { getBundleCache, setBundleCache } from './cache';
import { meDefine } from './define';
import { TrustedTypesWindow } from "trusted-types/lib";

const bundleIdMap: { [key: string]: BundleId } = {
    "@mecontrol/web-inline": "meInline",
    "@mecontrol/web-boot": "meBoot",
    "@mecontrol/web-core": "meCore"
};

const ttWindow = window as unknown as TrustedTypesWindow;
const trustedTypesLibrary = typeof ttWindow.trustedTypes !== "undefined" &&
    typeof ttWindow.trustedTypes.createPolicy !== "undefined"
    ? ttWindow.trustedTypes
    : { createPolicy: (_name: string, rules: any) => rules }; // Tinyfill for unsupported browsers

const ScriptURLPolicyMeControl = trustedTypesLibrary.createPolicy('MeControlScriptURL', {
    createScriptURL: (url: string): string => {
        if ((isValidURL(url) && new URL(url).origin === getRootDomainUrl()) || 
                (!isValidURL(url) && url.indexOf(getRootDomainUrl()) === 0)) {
            return url;
        }
        throw createError("MeControl Script url is not trusted. Does not match the expected origin.");
    }
});

/**
 * Download a Me Control bundle and return a Promise of its exports
 * @param bundleId The id of the bundle to download
 */
export function meImport<Bundle extends BundleId>(bundleId: Bundle): Promise<any> {
    try {
        if (bundleId in bundleIdMap) {
            bundleId = bundleIdMap[bundleId] as any;
        }

        updateBundleCache();
        let bundleMetadata = getBundleCache(bundleId);
        if (bundleMetadata) {
            return bundleMetadata.bundlePromise;
        }

        const bundlePromise = downloadScript(bundleId);
        setBundleCache(bundleId, bundlePromise);

        return bundlePromise;
    }
    catch (err) {
        return Promise.reject(err);
    }
}

function downloadScript(bundleId: BundleId): Promise<any> {
    return new Promise<any>((resolve, reject) => {
        const start = perfNow();
        const url = getJsUrl(bundleId);

        const head = document.getElementsByTagName('head')[0];
        let script: HTMLScriptElement | undefined = document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.charset = 'UTF-8';
        try {
            script.src = ScriptURLPolicyMeControl.createScriptURL(url) as string;
        } catch(error) {
            cleanup();
            reject(error);
        }

        const timeoutId = setTimeout(onScriptTimeout, ME.Config.timeoutMs);

        // IE9 - IE8 and below require using 'onreadystatechange'
        script.onload = onScriptComplete;
        script.onerror = onScriptError;

        script.crossOrigin = "anonymous";
        head.appendChild(script);

        function cleanup(): number {
            clearTimeout(timeoutId);
            if (script) {
                // Avoid memory leaks in IE
                script.onerror = script.onload = undefined as any;
                script = undefined;
            }

            return perfNow() - start;
        }

        function onScriptError(event: Event | string): void {
            const duration = cleanup();
            const errorMessage = 'Bundle failed to download/parse/compile';
            reportBundleLoadQos(bundleId, duration, url, true, errorMessage);
            reject(createError(`${errorMessage}: ${bundleId} (${url})`, false));
        }

        function onScriptTimeout(): void {
            const duration = cleanup();
            const errorMessage = 'Bundle download timed out';
            reportBundleLoadQos(bundleId, duration, url, true, errorMessage);
            reject(createError(`${errorMessage}: ${bundleId} (${url})`, true));
        }

        function onScriptComplete(event: Event | string): void {
            const duration = cleanup();

            // The bundle successfully loaded if the promise for its exports was processed by the define queue
            updateBundleCache();
            const metadata = getBundleCache(bundleId);
            const bundleExports = metadata && metadata.exports;
            if (bundleExports) {
                timeout(bundleExports, ME.Config.timeoutMs)
                    .then(exports => {
                        reportBundleLoadQos(bundleId, duration, url, false);
                        resolve(exports);
                    })
                    .catch((error: MeControlError) => {
                        const errorMessage = error.mcIsTimeout ?
                            'Resolving deps and exports timed out' :
                            'Resolving deps and exports failed';
                        reportBundleLoadQos(bundleId, duration, url, true, errorMessage);
                        reject(createError(`${errorMessage}: ${bundleId} (${url}). Inner error: ${error.message}`, error.mcIsTimeout));
                    });
            }
            else {
                let errorMessage: string;
                if (w.MeControlDefine != meDefine) {
                    errorMessage = 'window.MeControlDefine does not equal this bundle\'s meDefine function. Did you load Me Control twice?';
                }
                else {
                    errorMessage = 'Bundle failed to load exports';
                }

                reportBundleLoadQos(bundleId, duration, url, true, errorMessage);
                reject(createError(`${errorMessage}: ${bundleId} (${url})`, false));
            }
        }
    });
}

/**
 * Go through all currently queued bundles and import their dependencies so that
 * they can now be found in the bundle cache.
 */
function updateBundleCache(): void {
    while (queueLength()) {
        const queuedBundle = popBundle();
        if (queuedBundle) {

            // IE9 - Array.prototype.map is IE9 and above
            const bundleExports: any = {};
            const exports = Promise.all<any>(queuedBundle.deps
                .map(depId => depId === 'exports' ? bundleExports : meImport(depId as any)))
                .then(deps => { queuedBundle.factory(...deps); })
                .then(() => bundleExports);

            const metadata = getBundleCache(queuedBundle.id);
            if (!metadata) {
                // Possible for bundles that call MeControlDefine but aren't downloaded using meImport (e.g. web-inline)
                setBundleCache(queuedBundle.id, exports, exports);
            }
            else {
                metadata.exports = exports;
            }
        }
    }
}

/**
 * Get the full url for a javascript file
 * @param fileName The filename to get the full url of
 * @internal
 */
function getJsUrl(fileName: string): string {
    // EX: {GFX}/MeControl/Version/Market/meBoot[.min].js
    const config = ME.Config;
    return `${getRootDomainUrl()}/scripts/me/MeControl/${config.ver}/${config.mkt}/${fileName}${config.dbg ? '' : '.min'}.js`;
}

/**
 * Reports QoS related to a script load operation
 * @param bundleId The script id
 * @param duration The load duration
 * @param url The script url
 * @param failed True if it failed to load, false otherwise
 * @param message The message to include in telemetry
 */
function reportBundleLoadQos(bundleId: string, duration: number, url: string, failed: boolean, message?: string): void {
    logTelemetryEvent({
        eventType: 'OutgoingRequest',
        serviceOperation: 'DownloadScript',
        service: 'MeControl',
        url,
        success: !failed,
        duration,
        message
    });
}
